import { useState } from 'react'
import { useTranslation } from 'react-i18next'

import { BrowseLibraryModal } from '@/components/ui/browse-library-modal'
import { ComponentVisualState, DropzoneVisualState } from '@/components/ui/dropzone/dropzone.types'
import { useDropzoneHandlers, useWindowDrop } from '@/components/ui/dropzone/dropzone.utils'
import { popupFactory } from '@/factories'
import { useFilePicker } from '@/hooks'
import { optimizeImage } from '@/utils/file/optimize-image'

import {
  DEFAULT_ACCEPTED_TYPES,
  DEFAULT_MAX_FILE_SIZE,
  DEFAULT_MAX_FILES,
} from './file-upload.constants'
import * as S from './file-upload.styled'
import { FileUploadProps, ValidationError } from './file-upload.types'
import { buildAcceptedFilesText, buildSlotsText, verifyImageContent } from './file-upload.utils'
import { MaximizedUpload } from './file-upload-maximized'
import { MinimizedUpload } from './file-upload-minimized'

export const FileUpload = (props: FileUploadProps) => {
  const { t } = useTranslation(['common'])
  const {
    onValidationError,
    windowDrop = false,
    multiple = true,
    maxFiles: rawMaxFiles = DEFAULT_MAX_FILES,
    maxFileSize = DEFAULT_MAX_FILE_SIZE,
    maxOutputFileSize,
    acceptedTypes = DEFAULT_ACCEPTED_TYPES,
    size = 'medium',
    layout = 'maximized',
    previewShape,
    zoomable = true,
    imageOptimization,
    disabled = false,
    showBrowseLibrary = false,
    triggerMode = 'dropzone',
    label,
    required,
    description,
    existingImageUrl,
    className,
    initialSearch = '',
    error,
    withReservedFooter,
    uploadLabel = 'Upload file',
  } = props

  const isMaximized = layout === 'maximized'
  const isReplaceMode = isMaximized || !multiple
  const [dragState, setDragState] = useState<DropzoneVisualState>('none')
  const [uploading, setUploading] = useState(false)
  // Rejected picks never reach the form value, so the error lives here rather than in RHF.
  const [localError, setLocalError] = useState<string | null>(null)
  const isDisabled = disabled || uploading
  const displayedError = localError ?? error

  // `mode`, `files`, `onUpload` and `onChange` are the discriminated parts of
  // `FileUploadProps` — access them via `props` so `props.mode` narrows them.
  const { files } = props

  const pickerFiles = props.mode === 'auto' ? [] : props.files
  const pickerMaxFiles =
    props.mode === 'auto' ? Math.max(0, rawMaxFiles - files.length) : rawMaxFiles

  const handlePickerChange = async (newFiles: File[]) => {
    setLocalError(null)

    if (props.mode === 'auto') {
      if (!newFiles.length) return
      setUploading(true)
      try {
        const results = await props.onUpload(newFiles)
        props.onChange(isReplaceMode ? results : [...props.files, ...results])
      } catch {
        setLocalError(t('common:fileUpload.errors.uploadFailed'))
      } finally {
        setUploading(false)
      }
    } else {
      props.onChange(newFiles)
    }
  }

  const handleValidationError = (errors: ValidationError[]) => {
    setLocalError(errors[0]?.errors[0] ?? null)
    onValidationError?.(errors)
  }

  const {
    inputProps,
    open: handleInputClick,
    processFiles,
    processFile,
    atCapacity,
    remaining,
  } = useFilePicker({
    acceptedTypes,
    disabled: isDisabled,
    files: pickerFiles,
    maxFileSize,
    maxOutputFileSize,
    maxFiles: pickerMaxFiles,
    multiple,
    onChange: handlePickerChange,
    onValidationError: handleValidationError,
    replaceMode: isReplaceMode,
    transformFile: imageOptimization
      ? (file: File) => optimizeImage(file, { maxBytes: maxOutputFileSize, ...imageOptimization })
      : undefined,
    verifyFile: imageOptimization ? verifyImageContent : undefined,
  })

  const componentState: ComponentVisualState = isDisabled
    ? 'disabled'
    : dragState !== 'none'
      ? 'drag-over'
      : 'idle'

  const acceptedFilesText = buildAcceptedFilesText(
    acceptedTypes,
    isMaximized ? 1 : multiple ? rawMaxFiles : 1,
    maxFileSize,
    multiple,
  )

  const slotsText = !isMaximized ? buildSlotsText(multiple, remaining, files.length) : null

  // Unified onChange for sub-components (file removal). The narrowing here is
  // safe: auto mode always holds strings, manual mode always holds Files.
  const internalOnChange = (files: Array<File | string>) => {
    setLocalError(null)
    if (props.mode === 'auto') {
      props.onChange(files as string[])
    } else {
      props.onChange(files as File[])
    }
  }

  useWindowDrop({
    // Note: maximized accepts drops regardless of capacity since it replaces
    atCapacity: isMaximized ? false : atCapacity,
    disabled: isDisabled,
    enabled: windowDrop,
    onDragStateChange: (state) => setDragState(state),
    onDrop: (fileList) => processFiles(fileList),
  })

  const { handleDragEnter, handleDragLeave, handleDragOver, handleDrop } = useDropzoneHandlers({
    atCapacity,
    disabled: isDisabled,
    onDragStateChange: (state) => setDragState(state),
    onDrop: (fileList) => processFiles(fileList),
  })

  return (
    <S.Wrapper className={className}>
      <S.HiddenInput {...inputProps} />

      {isMaximized ? (
        <MaximizedUpload
          files={files}
          disabled={isDisabled}
          multiple={multiple}
          size={size}
          previewShape={previewShape}
          zoomable={zoomable}
          label={label}
          required={required}
          description={description}
          error={displayedError}
          existingImageUrl={existingImageUrl}
          acceptedFilesText={acceptedFilesText}
          uploadLabel={uploadLabel}
          onUploadClick={handleInputClick}
          onRemoveFile={() => internalOnChange([])}
        />
      ) : (
        <MinimizedUpload
          files={files}
          triggerMode={triggerMode}
          size={size}
          multiple={multiple}
          disabled={isDisabled}
          atCapacity={atCapacity}
          componentState={componentState}
          acceptedFilesText={acceptedFilesText}
          slotsText={slotsText}
          label={label}
          required={required}
          description={description}
          error={displayedError}
          withReservedFooter={withReservedFooter}
          uploadLabel={uploadLabel}
          showBrowseLibrary={showBrowseLibrary}
          onChange={internalOnChange}
          onBrowseLibrary={() =>
            popupFactory.open(BrowseLibraryModal, { initialSearch, onSelect: processFile })
          }
          onInputClick={handleInputClick}
          onDragEnter={handleDragEnter}
          onDragLeave={handleDragLeave}
          onDragOver={handleDragOver}
          onDrop={handleDrop}
        />
      )}
    </S.Wrapper>
  )
}
